import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Switch,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Modal from "../../../components/Modal";
import styles from "./style";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 36,
  height: 20,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#28AA4A",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    borderRadius: 8,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 10,
    opacity: 1,
    backgroundColor: "#BDBDBD",
    boxSizing: "border-box",
  },
}));

const TABLE_HEADERS = [
  "Name",
  "Type",
  "Pricing",
  "Available To",
  "Actions",
  "Active/Inactive",
];

const TopupManagementPage = ({
  topups = [],
  loadError = "",
  selectedCountry = "",
  purchasedPack = null,
  onCreateTopup,
  onViewSales,
  onEditTopup,
  onToggleTopupStatus,
  onDeleteTopup,
  onDeactivateInstead,
  onDismissPurchasedPack,
}) => {
  const [actionModal, setActionModal] = useState({
    open: false,
    action: "",
    topupId: "",
    currentStatus: true,
  });

  const openActionModal = (action, topupId, currentStatus) => {
    setActionModal({ open: true, action, topupId, currentStatus });
  };

  const closeActionModal = () => {
    setActionModal({ open: false, action: "", topupId: "", currentStatus: true });
  };

  const handleActionConfirm = () => {
    if (!actionModal.topupId) return;

    if (actionModal.action === "toggle" && onToggleTopupStatus) {
      onToggleTopupStatus(actionModal.topupId, actionModal.currentStatus);
    }

    if (actionModal.action === "delete" && onDeleteTopup) {
      onDeleteTopup(actionModal.topupId);
    }

    closeActionModal();
  };

  const isToggleAction = actionModal.action === "toggle";
  const modalTitle = isToggleAction
    ? `${actionModal.currentStatus ? "Deactivate" : "Activate"} Topup Pack`
    : "Delete Topup Pack";
  const modalBody = isToggleAction
    ? `Are you sure you want to ${actionModal.currentStatus ? "deactivate" : "activate"} this topup pack?`
    : "Are you sure you want to delete this topup pack?";
  const modalConfirm = isToggleAction
    ? actionModal.currentStatus ? "Deactivate" : "Activate"
    : "Delete";

  // GET /topup returns each pack's full pricing[] regardless of country, so
  // this is always available — unlike quantity, which is blank for every fixed
  // pack now that bundle size lives in its allowances instead.
  const renderPricing = (pack) => {
    const pricing = Array.isArray(pack?.pricing)
      ? pack.pricing.filter((p) => p?.isActive !== false)
      : [];
    if (pricing.length === 0) return "Not priced yet";
    return `${pricing.length} ${pricing.length === 1 ? "country" : "countries"} priced`;
  };

  const renderPackType = (pack) =>
    pack?.packType === "per_unit" ? "Per-unit" : pack?.packType === "fixed" ? "Fixed" : "—";

  const renderAvailability = (pack) => {
    const plans = pack?.availableToPlans;
    if (!Array.isArray(plans) || plans.length === 0) return "All plans";
    return `${plans.length} plan${plans.length === 1 ? "" : "s"}`;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box>
          <Typography sx={styles.title}>Topup Management</Typography>
          <Typography sx={styles.subtitle}>
            {selectedCountry
              ? `Add-on packs customers can buy on top of their plan (${selectedCountry})`
              : "Add-on packs customers can buy on top of their plan"}
          </Typography>
        </Box>
        <Box sx={styles.headerActions}>
          <Button variant="outlined" onClick={onViewSales} sx={styles.salesButton}>
            View Sales
          </Button>
          <Button variant="contained" onClick={onCreateTopup} sx={styles.addButton}>
            + Create New Topup
          </Button>
        </Box>
      </Box>

      {loadError && (
        <Alert severity="error" sx={styles.errorAlert}>
          {loadError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell key={header} sx={styles.tableHeaderCell}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {topups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={styles.emptyCell}>
                  <Typography>
                    {loadError
                      ? "Topup packs could not be loaded."
                      : selectedCountry
                        ? `No topup packs priced for ${selectedCountry} yet.`
                        : "No topup packs found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              topups.map((pack) => {
                const topupId = pack?._id || pack?.id;
                const isActive = pack?.isActive !== false;
                return (
                  <TableRow key={topupId}>
                    <TableCell sx={styles.tableCell}>{pack?.name || "—"}</TableCell>
                    <TableCell sx={styles.tableCell}>{renderPackType(pack)}</TableCell>
                    <TableCell sx={styles.tableCell}>{renderPricing(pack)}</TableCell>
                    <TableCell sx={styles.tableCell}>{renderAvailability(pack)}</TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Box sx={styles.actionContainer}>
                        <IconButton
                          size="small"
                          onClick={() => onEditTopup && onEditTopup(topupId)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openActionModal("delete", topupId)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <AntSwitch
                        checked={isActive}
                        onChange={(e) => {
                          e.stopPropagation();
                          openActionModal("toggle", topupId, isActive);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={actionModal.open}
        setOpen={closeActionModal}
        deletetText={modalConfirm}
        cancelText="Cancel"
        title={modalTitle}
        handleDelete={handleActionConfirm}
      >
        <Box>
          <Typography>{modalBody}</Typography>
        </Box>
      </Modal>

      {/* Backend refused the delete because the pack has been purchased.
          The backend's own message is shown and deactivate is offered — the
          delete is never silently converted. */}
      <Modal
        open={!!purchasedPack}
        setOpen={onDismissPurchasedPack}
        deletetText={purchasedPack?.isActive ? "Deactivate instead" : "Close"}
        cancelText="Cancel"
        title="Cannot Delete Topup Pack"
        handleDelete={
          purchasedPack?.isActive ? onDeactivateInstead : onDismissPurchasedPack
        }
      >
        <Box>
          <Typography>{purchasedPack?.message}</Typography>
          {purchasedPack?.isActive && (
            <Typography sx={{ mt: 1, fontSize: "13px", color: "#878787" }}>
              You can deactivate it instead so it stops being sold, without
              affecting topups customers have already bought.
            </Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default TopupManagementPage;
